/**
 * The tour overlay — "Show me" (docs/SYSTEM.md → Show me tours) and a page's
 * first-visit tour (src/features/shared/tours): goes to each step's screen,
 * waits for its part to appear and settle, and dims everything else to 20%
 * black, with a small card saying what it is.
 *
 * Not a modal. The spotlight is a hole in a shadow that ignores the pointer,
 * so the changed part — and the rest of the page — stay usable; trying the
 * thing is half of reviewing it. Mounted once in the root layout, so it
 * carries on across the route changes it causes.
 */
import { useRouter } from '@tanstack/react-router'
import { ArrowLeft, ArrowRight, Check, X } from 'lucide-react'
import { type CSSProperties, useEffect, useLayoutEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { Button } from '@/components/ui/button'
import { resolveTourRoute } from './placeholders'
import { type ActiveTour, endTour, goToStep, useActiveTour } from './tour-store'
import { findTourTarget } from './tour-target'

/** How long to look for the changed part before saying so (never a dead end). */
const FIND_FOR_MS = 5_000
/** A page tour's optional part (the first row of a list that may be empty): a shorter look. */
const OPTIONAL_FOR_MS = 1_500
/**
 * Looked for on a timer, not per frame: a browser pauses frames in a tab
 * that isn't showing, and the search would stall. Lit once it has kept still
 * for three looks — a list still loading moves it.
 */
const LOOK_EVERY_MS = 50
const STILL_LOOKS = 3
/** Room around the part, and between it and the card (docs/LAYOUT.md spacing). */
const PAD = 8
const GAP = 12
const EDGE = 16
const CARD_WIDTH = 360

type Phase = 'finding' | 'shown' | 'missing'

interface Box {
  top: number
  left: number
  width: number
  height: number
}

const boxOf = (element: Element): Box => {
  const rect = element.getBoundingClientRect()
  return { top: rect.top, left: rect.left, width: rect.width, height: rect.height }
}

const sameBox = (a: Box | null, b: Box | null) =>
  a === b ||
  (a !== null &&
    b !== null &&
    a.top === b.top &&
    a.left === b.left &&
    a.width === b.width &&
    a.height === b.height)

export function TourOverlay() {
  const tour = useActiveTour()
  if (!tour) return null
  return createPortal(<Tour tour={tour} />, document.body)
}

function Tour({ tour }: { tour: ActiveTour }) {
  const router = useRouter()
  const step = tour.steps[tour.index]
  const [phase, setPhase] = useState<Phase>('finding')
  const [box, setBox] = useState<Box | null>(null)
  const [cardHeight, setCardHeight] = useState(0)
  const cardRef = useRef<HTMLDivElement>(null)
  const isLast = tour.index === tour.steps.length - 1

  // Each step: fill in its record, go there, find the part, light it.
  useEffect(() => {
    if (!step) return
    let cancelled = false
    let frame = 0
    let timer = 0
    setPhase('finding')
    setBox(null)

    const follow = (element: Element | null) => {
      // While lit, follow the part as the page scrolls or reflows.
      const tick = () => {
        if (cancelled) return
        const current = element?.isConnected ? element : findTourTarget(step.target)
        element = current
        const next = current ? boxOf(current) : null
        setBox((previous) => (sameBox(previous, next) ? previous : next))
        frame = requestAnimationFrame(tick)
      }
      tick()
    }

    void (async () => {
      const href = await resolveTourRoute(step.route)
      if (cancelled) return
      if (!href) {
        setPhase('missing')
        return
      }
      const here = `${window.location.pathname}${window.location.search}`
      if (href !== here) await router.navigate({ href })

      const deadline = performance.now() + (step.optional ? OPTIONAL_FOR_MS : FIND_FOR_MS)
      let last: Box | null = null
      let still = 0
      const find = () => {
        if (cancelled) return
        const element = findTourTarget(step.target)
        if (element) {
          const next = boxOf(element)
          still = next.width > 0 && next.height > 0 && sameBox(last, next) ? still + 1 : 0
          last = next
          if (still >= STILL_LOOKS) {
            // At once, not smoothly: a smooth scroll can stall in a tab
            // that isn't showing, leaving the spotlight off the screen.
            element.scrollIntoView({ block: 'center', behavior: 'auto' })
            setPhase('shown')
            follow(element)
            return
          }
        }
        if (performance.now() > deadline) {
          // Not there right now (an empty list): go on without it.
          if (step.optional) goToStep(tour.index + 1)
          else setPhase('missing')
          return
        }
        timer = window.setTimeout(find, LOOK_EVERY_MS)
      }
      find()
    })()

    return () => {
      cancelled = true
      cancelAnimationFrame(frame)
      window.clearTimeout(timer)
    }
  }, [step, router, tour.index])

  // Focus moves to the card with each step, so the keyboard and a screen
  // reader are where the tour is.
  useEffect(() => {
    if (phase !== 'finding') cardRef.current?.focus({ preventScroll: true })
  }, [phase])

  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') endTour()
    }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [])

  useLayoutEffect(() => {
    const card = cardRef.current
    if (!card) return
    const observer = new ResizeObserver(() => setCardHeight(card.offsetHeight))
    observer.observe(card)
    return () => observer.disconnect()
  }, [])

  if (!step) return null

  const page = tour.kind === 'page'
  const done = () => {
    endTour()
    // A page tour ends where it is; "Show me" goes back to the change log.
    if (!page) void router.navigate({ href: tour.returnTo })
  }
  const lit = phase === 'shown' && box ? box : null

  return (
    <>
      {lit ? (
        <div
          aria-hidden="true"
          data-tour-spotlight=""
          className="pointer-events-none fixed z-50 rounded-lg shadow-spotlight"
          style={{
            top: lit.top - PAD,
            left: lit.left - PAD,
            width: lit.width + PAD * 2,
            height: lit.height + PAD * 2,
          }}
        />
      ) : phase === 'missing' ? (
        <div
          aria-hidden="true"
          className="pointer-events-none fixed inset-0 z-50 bg-spotlight-dim"
        />
      ) : null}

      <div
        ref={cardRef}
        role="dialog"
        aria-modal="false"
        aria-label={page ? `Tour: ${tour.title}` : `Show me: ${tour.title}`}
        tabIndex={-1}
        data-tour-card=""
        className="fixed z-50 flex w-auto flex-col gap-3 rounded-xl border bg-popover p-4 text-popover-foreground shadow-floating outline-none md:w-90"
        style={placeCard(lit, cardHeight)}
      >
        <div className="flex items-start justify-between gap-3">
          <p className="pt-2 text-sm text-muted-foreground">
            {page ? `${tour.title} · ` : ''}Step {tour.index + 1} of {tour.steps.length}
          </p>
          <Button variant="ghost" size="icon" aria-label="End tour" onClick={endTour}>
            <X aria-hidden="true" />
          </Button>
        </div>
        <p aria-live="polite" className="text-sm">
          {phase === 'missing'
            ? page
              ? 'This part isn’t on the page right now.'
              : 'Couldn’t find this change on the page.'
            : phase === 'finding'
              ? page
                ? 'Finding it…'
                : 'Finding this change…'
              : step.caption}
        </p>
        <div className="flex justify-between gap-3">
          {page && tour.index === 0 ? (
            // A first visit can always be skipped; Back takes over from step 2.
            <Button variant="ghost" onClick={endTour}>
              Skip tour
            </Button>
          ) : (
            <Button
              variant="outline"
              disabled={tour.index === 0}
              onClick={() => goToStep(tour.index - 1)}
            >
              <ArrowLeft aria-hidden="true" />
              Back
            </Button>
          )}
          {isLast ? (
            <Button onClick={done}>
              <Check aria-hidden="true" />
              Done
            </Button>
          ) : (
            <Button
              variant={phase === 'missing' ? 'outline' : 'default'}
              onClick={() => goToStep(tour.index + 1)}
            >
              {phase === 'missing' ? 'Skip' : 'Next'}
              <ArrowRight aria-hidden="true" />
            </Button>
          )}
        </div>
      </div>
    </>
  )
}

/**
 * Under the part when it fits, over it when that fits, else the bottom of the
 * screen — always on a phone, where there is no room beside anything, and
 * whenever the part is scrolled out of sight, so the card never is.
 */
function placeCard(lit: Box | null, cardHeight: number): CSSProperties {
  const width = window.innerWidth
  const height = window.innerHeight
  const bottom: CSSProperties = { bottom: EDGE, left: EDGE, right: width < 768 ? EDGE : undefined }
  const inView = lit !== null && lit.top < height && lit.top + lit.height > 0
  if (!lit || !inView || width < 768) return bottom

  const left = Math.min(Math.max(lit.left - PAD, EDGE), width - CARD_WIDTH - EDGE)
  const below = lit.top + lit.height + PAD + GAP
  if (below + cardHeight <= height - EDGE) return { top: below, left }
  const above = lit.top - PAD - GAP - cardHeight
  if (above >= EDGE) return { top: above, left }
  return { bottom: EDGE, right: EDGE }
}
